import { tempoApi } from '@/lib/api/tempoApi';
import type { Plan, PlanCreateRequest, PlanUpdateRequest } from '@/lib/api/types/adaptivePlanner';
import type {
  PlanAlert,
  PlanUsersPermissionResponse,
  PlanUsersPermissionsSubmitData,
  SyncWithPortfolioResponse,
} from '../types/adaptive-planner/plan';

export const getPlans = async ({ limit }: { limit?: number } = {}): Promise<Plan[]> => {
  const searchParams = new URLSearchParams();

  if (limit !== undefined) {
    searchParams.set('limit', limit.toString());
  }

  const url = `api/v1/adaptive-planner/plans${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  const response = await tempoApi().get(url).json<Plan[]>();
  return response;
};

const getPlan = async ({ planId }: { planId: number }): Promise<Plan> => {
  const response = await tempoApi().get(`api/v1/adaptive-planner/plans/${planId}`).json<Plan>();
  return response;
};

const createPlan = async ({ data }: { data: PlanCreateRequest }): Promise<Plan> => {
  const response = await tempoApi()
    .post('api/v1/adaptive-planner/plans', {
      json: data,
    })
    .json<Plan>();
  return response;
};

const updatePlan = async ({
  planId,
  data,
}: {
  planId: number;
  data: PlanUpdateRequest;
}): Promise<Plan> => {
  const response = await tempoApi()
    .patch(`api/v1/adaptive-planner/plans/${planId}`, { json: data })
    .json<Plan>();
  return response;
};

const deletePlan = async ({ planId }: { planId: number }): Promise<void> => {
  await tempoApi().delete(`api/v1/adaptive-planner/plans/${planId}`);
};

const getUsersPermissionsForPlan = async ({
  planId,
}: {
  planId: number;
}): Promise<PlanUsersPermissionResponse> => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/permissions`)
    .json<PlanUsersPermissionResponse>();
  return response;
};

const setUsersPermissionsForPlan = async ({
  planId,
  data,
}: {
  planId: number;
  data: PlanUsersPermissionsSubmitData;
}) => {
  const response = await tempoApi()
    .put(`api/v1/adaptive-planner/plans/${planId}/permissions`, {
      json: data,
    })
    .json<void>();

  return response;
};

const syncWithPortfolio = async ({
  planId,
}: {
  planId: number;
}): Promise<SyncWithPortfolioResponse> => {
  return await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/portfolio-sync`)
    .json<SyncWithPortfolioResponse>();
};

const getAlerts = async ({
  planId,
  alertTypes,
}: {
  planId: number;
  alertTypes?: PlanAlert['alertType'][];
}): Promise<PlanAlert[]> => {
  const searchParams = new URLSearchParams();

  if (alertTypes && alertTypes.length > 0) {
    alertTypes.forEach((type) => {
      searchParams.append('alertTypes', type);
    });
  }

  const url = `api/v1/adaptive-planner/plans/${planId}/alerts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  return await tempoApi().get(url).json<PlanAlert[]>();
};

export const AdaptivePlannerPlan = {
  getPlan,
  getPlans,
  updatePlan,
  deletePlan,
  createPlan,
  getUsersPermissionsForPlan,
  setUsersPermissionsForPlan,
  syncWithPortfolio,
  getAlerts,
} as const;
